"use client";

import { Link, Image, usePathname } from "@/src/lib/next-compat";
import { useClerk } from "@clerk/clerk-react";
import {
    Home,
    Menu,
    X,
    Upload,
    GraduationCap,
    UserPlus,
    Users,
    Building2,
    Coins,
    Cpu,
    Landmark,
    Sparkles,
} from "lucide-react";

import { UploadLessonModal } from "@/components/admin/UploadLessonModal";
import { useAdmin } from "@/components/admin/AdminContext";

interface AdminShellProps {
    children: React.ReactNode;
    user: {
        fullName?: string | null;
        primaryEmailAddress?: { emailAddress: string } | null;
        imageUrl: string;
    } | null;
    mockUser?: { // Optional mock user override data
        fullName: string;
        email: string;
        imageUrl: string;
    };
    sidebarOpen: boolean;
    setSidebarOpen: (open: boolean) => void;
}

const navigationGroups = [
    {
        name: "General",
        items: [
            { name: "Dashboard", href: "/admin/dashboard", icon: Home },
        ]
    },
    {
        name: "Finance",
        items: [
            { name: "Payments", href: "/admin/payments", icon: Landmark },
        ]
    },
    {
        name: "Enterprise",
        items: [
            { name: "B2B Approvals", href: "/admin/organizations", icon: Building2 },
            { name: "Custom Courses", href: "/admin/organizations?tab=course_requests", icon: Sparkles },
            { name: "AI Credits & Pricing", href: "/admin/credits", icon: Coins },
            { name: "AI Models & Costs", href: "/admin/ai-models", icon: Cpu },
        ]
    },
    {
        name: "Content",
        items: [
            { name: "Courses", href: "/admin/courses", icon: GraduationCap },
        ]
    },
    {
        name: "Team",
        items: [
            { name: "Team Directory", href: "/admin/members", icon: Users },
            { name: "Invitations", href: "/admin/invitations", icon: UserPlus },
        ]
    }
];

function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(" ");
}

export function AdminShell({
    children,
    user,
    mockUser,
    sidebarOpen,
    setSidebarOpen,
}: AdminShellProps) {
    const pathname = usePathname();
    const activePath = pathname ?? "";
    const { signOut } = useClerk();

    const handleSignOut = () => {
        void signOut().then(() => {
            window.location.href = "/";
        });
    };
    // A nav item is active when the current path is it, or a sub-route of it — so
    // nested pages like /admin/courses/builder keep "Courses" highlighted.
    const isActive = (href: string) => {
        if (href.includes("?")) {
            const [base, query] = href.split("?");
            return (
                activePath === base &&
                typeof window !== "undefined" &&
                window.location.search.includes(query)
            );
        }
        if (href === "/admin/organizations") {
            return (
                activePath === href &&
                typeof window !== "undefined" &&
                !window.location.search.includes("tab=course_requests")
            );
        }
        return activePath === href || activePath.startsWith(href + "/");
    };
    const { isUploadModalOpen, setIsUploadModalOpen } = useAdmin();

    // Determine user display info
    const userName = mockUser ? mockUser.fullName : (user?.fullName || "Admin User");
    const userEmail = mockUser ? mockUser.email : (user?.primaryEmailAddress?.emailAddress || "admin@inara.ai");
    const userImage = mockUser ? mockUser.imageUrl : (user?.imageUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(userName)}&background=random`);

    return (
        <div className="min-h-screen bg-slate-50 font-sans">
            {/* Mobile Sidebar Overlay */}
            {sidebarOpen && (
                <div className="relative z-50 md:hidden" role="dialog" aria-modal="true">
                    <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm transition-opacity" onClick={() => setSidebarOpen(false)} />

                    <div className="fixed inset-0 flex">
                        <div className="relative mr-16 flex w-full max-w-xs flex-1">
                            <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                                <button type="button" className="-m-2.5 p-2.5" onClick={() => setSidebarOpen(false)}>
                                    <span className="sr-only">Close sidebar</span>
                                    <X className="h-6 w-6 text-white" aria-hidden="true" />
                                </button>
                            </div>

                            <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4">
                                <div className="flex h-16 shrink-0 items-center">
                                    {/* Mobile Sidebar Logo */}
                                    <div className="relative mt-8 mb-4 flex items-center gap-2.5">
                                        <span className="shrink-0 text-[11px] font-black uppercase tracking-[0.2em] text-indigo-600">
                                            Admin
                                        </span>
                                        <Link to="/" className="relative flex min-w-0 items-center" onClick={() => setSidebarOpen(false)}>
                                            <img                                                 src="/images/logo-v2.png"
                                                alt="inaraX"
                                                width={400}
                                                height={120}
                                                className="h-8 w-auto object-contain object-left"
                                            />
                                        </Link>
                                    </div>
                                </div>



                                <nav className="flex flex-1 flex-col">
                                        <ul role="list" className="list-none ml-0 flex flex-1 flex-col gap-y-7">
                                        {navigationGroups.map((group) => (
                                            <li key={group.name}>
                                                <div className="text-slate-400 text-[10px] font-extrabold uppercase tracking-wider mb-2 px-2">
                                                    {group.name}
                                                </div>
                                                <ul role="list" className="list-none ml-0 -mx-2 space-y-1">
                                                    {group.items.map((item) => {
                                                        const is_active = isActive(item.href);
                                                        return (
                                                            <li key={item.name}>
                                                                <Link
                                                                    to={item.href}
                                                                    onClick={() => setSidebarOpen(false)}
                                                                    className={classNames(
                                                                        is_active
                                                                            ? 'bg-indigo-50 text-indigo-700'
                                                                            : 'text-slate-900 hover:text-black hover:bg-slate-100',
                                                                        'group flex gap-x-3 rounded-lg p-2 text-sm leading-6 font-bold'
                                                                    )}
                                                                >
                                                                    <item.icon
                                                                        className={classNames(
                                                                            is_active
                                                                                ? 'text-indigo-600'
                                                                                : 'text-slate-600 group-hover:text-black',
                                                                            'h-6 w-6 shrink-0'
                                                                        )}
                                                                        aria-hidden="true"
                                                                    />
                                                                    {item.name}
                                                                </Link>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </li>
                                        ))}
                                    </ul>
                                </nav>
                                <div className="mt-auto border-t border-slate-200 pt-4">
                                    <div className="flex items-center gap-3 px-2">
                                        <img                                             className="h-8 w-8 rounded-full bg-slate-100"
                                            src={userImage}
                                            alt=""
                                            width={32}
                                            height={32}
                                        />
                                        <div className="flex flex-col">
                                            <span className="text-sm font-bold text-slate-900" aria-hidden="true">{userName}</span>
                                            <span className="text-xs text-slate-500 truncate max-w-[160px]">{userEmail}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="flex h-screen overflow-hidden">
                {/* Sidebar (Desktop) */}
                <div className="hidden md:flex md:flex-shrink-0">
                    <div className="flex w-64 flex-col">
                        <div className="flex min-h-0 flex-1 flex-col bg-white border-r border-slate-200">
                            <div className="flex flex-1 flex-col overflow-y-auto pt-4 pb-4 px-4">
                                {/* Top "Inara" Brand Block */}
                                <div className="mb-6 flex items-center gap-2.5 px-2">
                                    <span className="shrink-0 text-[11px] font-black uppercase tracking-[0.2em] text-indigo-600">
                                        Admin
                                    </span>
                                    <Link to="/" className="relative flex min-w-0 items-center transition-opacity hover:opacity-80">
                                        <img                                             src="/images/logo-v2.png"
                                            alt="inaraX"
                                            width={600}
                                            height={180}
                                            className="h-8 w-auto object-contain object-left md:h-10"
                                        />
                                    </Link>
                                </div>

                                <nav className="mt-2 flex-1 flex flex-col gap-y-6">
                                    {navigationGroups.map((group) => (
                                        <div key={group.name} className="space-y-1">
                                            <div className="px-2 mb-2">
                                                <p className="text-slate-400 text-[10px] font-extrabold uppercase tracking-wider">{group.name}</p>
                                            </div>
                                            {group.items.map((item) => {
                                                const is_active = isActive(item.href);
                                                return (
                                                    <Link
                                                        key={item.name}
                                                        to={item.href}
                                                        className={classNames(
                                                            is_active
                                                                ? "bg-indigo-50 text-indigo-700"
                                                                : "text-slate-900 hover:bg-slate-100 hover:text-black",
                                                            "group flex items-center rounded-lg px-3 py-2 text-sm font-bold transition-all duration-200 ease-in-out"
                                                        )}
                                                    >
                                                        <item.icon
                                                            className={classNames(
                                                                is_active
                                                                    ? "text-indigo-600"
                                                                    : "text-slate-600 group-hover:text-black",
                                                                "mr-3 h-5 w-5 flex-shrink-0 transition-colors stroke-[2px]"
                                                            )}
                                                            aria-hidden="true"
                                                        />
                                                        {item.name}
                                                    </Link>
                                                );
                                            })}
                                        </div>
                                    ))}
                                </nav>

                                {/* Upload Lesson Button */}
                                <div className="mt-auto pt-4">
                                    <button
                                        onClick={() => setIsUploadModalOpen(true)}
                                        className="w-full flex items-center gap-3 px-3 py-3 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-all duration-200 shadow-lg shadow-blue-500/20"
                                    >
                                        <Upload className="h-6 w-6" />
                                        Upload Course
                                    </button>
                                </div>
                            </div>

                            {/* Logout and User Profile Section */}
                            <div className="p-4 border-t border-slate-200 space-y-4">
                                {/* User Profile Card */}
                                <div className="flex items-center gap-3 rounded-xl p-2 transition-colors hover:bg-slate-50">
                                    <img                                         className="h-9 w-9 rounded-full bg-slate-100 object-cover border border-slate-200"
                                        src={userImage}
                                        alt={userName}
                                        width={36}
                                        height={36}
                                    />
                                    <div className="flex flex-col min-w-0">
                                        <p className="text-sm font-bold text-slate-900 truncate">{userName}</p>
                                        <p className="text-xs font-medium text-slate-700 truncate">{userEmail}</p>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    onClick={handleSignOut}
                                    className="flex items-center w-full px-2 py-2 text-sm font-bold text-slate-700 hover:text-red-700 transition-colors"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="mr-3 h-5 w-5">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
                                    </svg>
                                    Log Out
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main content */}
                <div className="flex flex-1 flex-col overflow-hidden bg-slate-50">
                    <main className="relative flex-1 overflow-y-auto focus:outline-none">
                        <button
                            type="button"
                            className="fixed left-4 top-4 z-30 rounded-lg border border-slate-200 bg-white p-2.5 text-slate-700 shadow-md md:hidden"
                            onClick={() => setSidebarOpen(true)}
                            aria-label="Open sidebar"
                        >
                            <Menu className="h-5 w-5" aria-hidden />
                        </button>

                        <div className="pb-6 pt-14 md:pb-8 md:pt-0">
                            <div className="w-full px-4 sm:px-6 md:px-8">
                                {children}
                            </div>
                        </div>
                    </main>
                </div>
            </div>

            {/* Upload Modal */}
            <UploadLessonModal
                isOpen={isUploadModalOpen}
                onClose={() => setIsUploadModalOpen(false)}
                onSuccess={() => {
                    // Optionally refresh data or show notification
                    console.log("Upload successful!");
                }}
            />
        </div>
    );
}
